/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  ListItem,
  ListItemButton,
  Typography,
  Box,
  Grid,
  styled,
  useTheme,
} from '@mui/material';
import type { TFunction } from 'i18next';
import type { FC } from 'react';
import React, { useRef } from 'react';
import type { RowComponentProps } from 'react-window';
import { List } from 'react-window';
import { ToggleMenu, dateUtils } from '@opengeoweb/shared';
import { ChevronDown, Delete, Edit } from '@opengeoweb/theme';
import { getShareDrawingMenuItems } from './shareDrawing';
import type { DrawingListItem } from '../../store/warningsDrawings/types';
import type { WarningType } from '../../store/warningsForm/types';
import { DATE_FORMAT_HEADER } from '../../utils/constants';
import { useWarningsTranslation } from '../../utils/i18n';
import { getUtcDateLabel } from '../../utils/warningUtils';

export const getFeatureName = (
  feature: GeoJSON.Feature,
  index: number,
  t: TFunction,
): string => {
  const name = feature.properties?.objectName ?? feature.properties?.name;
  return name
    ? String(name)
    : t('area-drawing-manager-object-name', { number: index + 1 });
};

const RowComponent = ({
  index,
  style,
  listInclHeaders,
  headerList,
  drawings,
  onClickDrawing,
  onClickEdit,
  onClickDelete,
  onClickShare,
  onToggleExpand,
  onHoverDrawing,
  onHoverDrawingEnd,
  onHoverDrawingFeature,
  onHoverDrawingFeatureEnd,
  expandedDrawing,
  summaryHeight,
  activeDrawing,
  t,
}: RowComponentProps<{
  listInclHeaders: { isDrawing: boolean; index: number }[];
  headerList: string[];
  drawings: DrawingListItem[];
  onClickDrawing: (drawing: DrawingListItem) => void;
  onClickEdit: (drawingId: string) => void;
  onClickDelete: (drawingId: string) => void;
  onClickShare: (drawing: DrawingListItem, type: WarningType) => void;
  onToggleExpand: (drawingId: string) => void;
  onHoverDrawing: (drawing: DrawingListItem) => void;
  onHoverDrawingEnd: () => void;
  onHoverDrawingFeature: (
    drawing: DrawingListItem,
    featureIndex: number,
  ) => void;
  onHoverDrawingFeatureEnd: (drawing: DrawingListItem) => void;
  expandedDrawing: string;
  summaryHeight: number;
  activeDrawing: string;
  t: TFunction;
}>): React.ReactElement => {
  const theme = useTheme();
  const listItem = listInclHeaders[index];
  if (!listItem.isDrawing) {
    const header = headerList[listItem.index];
    return (
      <Typography
        key={listItem.index}
        style={style}
        sx={{
          paddingLeft: 2.5,
          paddingBottom: 0.5,
          paddingTop: 1,
          opacity: 0.67,
          lineHeight: '16px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
        variant="caption"
      >
        {header}
      </Typography>
    );
  }

  const drawing = drawings[listItem.index];
  const features = drawing.geoJSON?.features ?? [];

  return (
    <ContainerDiv style={style}>
      <Accordion
        data-testid={`drawing-accordion-${drawing.id}`}
        disableGutters
        expanded={expandedDrawing === drawing.id}
        onMouseEnter={(): void => onHoverDrawing(drawing)}
        onMouseLeave={(): void => onHoverDrawingEnd()}
        sx={{
          '.MuiAccordionSummary-content': {
            padding: '0px',
            margin: '0px',
            height: '100%',
          },
          '&.MuiAccordion-root': {
            boxShadow: 0,
          },
          '&.MuiAccordion-root:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          component="div"
          data-testid={`drawing-summary-${drawing.id}`}
          expandIcon={
            <ChevronDown
              data-testid={`drawing-expand-${drawing.id}`}
              onClick={(): void => {
                onToggleExpand(drawing.id);
              }}
              style={{
                fill: `${theme.palette.geowebColors.buttons.icon.default.color}`,
              }}
            />
          }
          sx={{
            height: `${summaryHeight}px`,
            minHeight: 'unset',
            padding: '0 4px 0 0',
            '&.MuiAccordionSummary-root:hover': {
              backgroundColor: 'geowebColors.cards.cardContainerMouseOver',
            },
          }}
        >
          <ListItem
            disablePadding
            key={drawing.id}
            secondaryAction={
              <Grid container rowSpacing={1}>
                <Grid className="drawingActions">
                  <ToggleMenu
                    menuPosition="bottom"
                    {...(drawing.scope === 'user'
                      ? {
                          menuTitle: t('area-drawing-manager-options-menu'),
                          menuItems: [
                            {
                              text: t('area-drawing-manager-button-edit'),
                              action: (): void => {
                                onClickEdit(drawing.id);
                              },
                              icon: <Edit />,
                            },
                            {
                              text: t('area-drawing-manager-button-delete'),
                              action: (): void => {
                                onClickDelete(drawing.id);
                              },
                              icon: <Delete />,
                            },
                          ],
                        }
                      : {})}
                    secondaryMenuItems={getShareDrawingMenuItems(t, (type) => {
                      onClickShare(drawing, type);
                    })}
                    secondaryMenuTitle={t('area-drawing-manager-share-menu')}
                    tooltipTitle={t('area-drawing-manager-options-menu')}
                  />
                </Grid>
              </Grid>
            }
          >
            <ListItemButton
              data-testid={`drawing-item-${drawing.id}`}
              onClick={(): void => {
                onClickDrawing(drawing);
              }}
              selected={activeDrawing === drawing.id}
              sx={{
                padding: 1,
                height: '44px',
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Stack>
                <Typography
                  sx={{
                    lineHeight: '16px',
                    maxHeight: '32px',
                    overflow: 'hidden',
                  }}
                  variant="caption"
                >
                  {drawing.objectName}
                </Typography>
                <Typography
                  sx={{
                    opacity: 0.67,
                    lineHeight: '16px',
                    maxHeight: '32px',
                    overflow: 'hidden',
                  }}
                  variant="caption"
                >
                  {drawing.scope === 'global' && 'Global: '}
                  {getUtcDateLabel(drawing.lastUpdatedTime)}
                </Typography>
              </Stack>
            </ListItemButton>
          </ListItem>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '8px 8px 8px 16px' }}>
          {features.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {features.map((feature, featureIndex) => (
                <Box
                  data-testid={`drawing-feature-${drawing.id}-${featureIndex}`}
                  key={`${drawing.id}-${getFeatureName(feature, featureIndex, t)}`}
                  onMouseEnter={(): void =>
                    onHoverDrawingFeature(drawing, featureIndex)
                  }
                  onMouseLeave={(): void => onHoverDrawingFeatureEnd(drawing)}
                  sx={{
                    alignItems: 'center',
                    backgroundColor: 'geowebColors.background.surface',
                    display: 'flex',
                    minHeight: `${FEATURE_ITEM_SIZE - 4}px`,
                    ':hover': {
                      backgroundColor:
                        'geowebColors.cards.cardContainerMouseOver',
                    },
                    paddingLeft: '8px',
                  }}
                >
                  <Typography
                    sx={{
                      lineHeight: '16px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    variant="caption"
                  >
                    {getFeatureName(feature, featureIndex, t)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2">
              {t('area-drawing-manager-no-objects')}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </ContainerDiv>
  );
};

const DRAWING_ITEM_SIZE = 50;
const DRAWING_ITEM_SIZE_SMALL_WIDTH = 72;
const HEADER_ITEM_SIZE = 28;
const FEATURE_ITEM_SIZE = 32;
const ROW_GAP = 3;
const BREAKPOINT = 320;
export const DEFAULT_ADD_BUTTON_HEIGHT = 80;
export const DEFAULT_AREA_DRAWING_MANAGER_TAB_HEIGHT = 40;
export const DEFAULT_AREA_DRAWING_MANAGER_MIN_SIZE = {
  width: 260,
  height: 320,
};

export { BUTTON_AIRMET, BUTTON_SIGMET } from './shareDrawing';

type ListInclHeadersType = { isDrawing: boolean; index: number }[];

export const getListInclHeaders = (
  drawings: DrawingListItem[],
): { listInclHeaders: ListInclHeadersType; headerList: string[] } => {
  const listInclHeaders: ListInclHeadersType = [];
  const headerList: string[] = [];
  // Construct list incl day headers
  drawings.forEach((drawing, index) => {
    const header = dateUtils.dateToString(
      dateUtils.utc(drawing.lastUpdatedTime),
      DATE_FORMAT_HEADER,
    )!;
    const isNewDay = !headerList.find((item) => item === header);
    if (isNewDay) {
      const newLength = headerList.push(header);
      listInclHeaders.push({ isDrawing: false, index: newLength - 1 });
    }
    listInclHeaders.push({ isDrawing: true, index });
  });

  return { listInclHeaders, headerList };
};

const ContainerDiv = styled('div')(() => ({
  boxSizing: 'border-box',
  paddingBottom: `${ROW_GAP}px`,
  '.drawingActions': {
    width: '100%',
    minWidth: '100%',
  },
  '& .MuiListItemSecondaryAction-root': {
    width: '24px',
    right: '8px',
  },
  '& .MuiPaper-root': {
    height: '100%',
  },
  '& .MuiListItem-root': {
    height: '100%',
  },
  '& .MuiListItemButton-root': {
    height: '100%',
  },
  '& .MuiStack-root': {
    paddingRight: '32px',
    width: '100%',
    overflowWrap: 'anywhere',
    overflow: 'hidden',
  },
  [`@container drawingList (min-width: ${BREAKPOINT}px)`]: {
    '.drawingActions': {
      width: '50%',
      minWidth: '50%',
    },
    '& .MuiStack-root': {
      paddingRight: '64px',
    },
  },
}));

export const Drawings: FC<{
  drawings: DrawingListItem[];
  onClickDrawing: (drawing: DrawingListItem) => void;
  onClickEdit: (drawingId: string) => void;
  onClickDelete: (drawingId: string) => void;
  onClickShare: (drawing: DrawingListItem, type: WarningType) => void;
  onHoverDrawing: (drawing: DrawingListItem) => void;
  onHoverDrawingEnd: () => void;
  onHoverDrawingFeature?: (
    drawing: DrawingListItem,
    featureIndex: number,
  ) => void;
  onHoverDrawingFeatureEnd?: (drawing: DrawingListItem) => void;
  activeDrawing: string;
  isLoading?: boolean;
  dialogWidth?: number;
}> = ({
  drawings,
  onClickDrawing,
  onClickEdit,
  onClickDelete,
  onClickShare,
  onHoverDrawing,
  onHoverDrawingEnd,
  onHoverDrawingFeature = (): void => {},
  onHoverDrawingFeatureEnd = (): void => {},
  activeDrawing,
  isLoading = false,
  dialogWidth = DEFAULT_AREA_DRAWING_MANAGER_MIN_SIZE.width,
}) => {
  const { t } = useWarningsTranslation();
  const { listInclHeaders, headerList } = getListInclHeaders(drawings || []);
  const [expandedDrawing, setExpandedDrawing] = React.useState<string>('');

  const summaryHeight =
    dialogWidth < BREAKPOINT
      ? DRAWING_ITEM_SIZE_SMALL_WIDTH
      : DRAWING_ITEM_SIZE;

  const getItemSize = (width: number, index: number): number => {
    const item = listInclHeaders[index];
    if (!item.isDrawing) {
      return HEADER_ITEM_SIZE;
    }

    const drawingItemSize =
      width < BREAKPOINT ? DRAWING_ITEM_SIZE_SMALL_WIDTH : DRAWING_ITEM_SIZE;
    const drawing = drawings[item.index];

    if (expandedDrawing !== drawing.id) {
      return drawingItemSize + ROW_GAP;
    }

    const featureCount = drawing.geoJSON?.features.length ?? 0;
    return (
      drawingItemSize +
      Math.max(featureCount, 1) * FEATURE_ITEM_SIZE +
      16 +
      ROW_GAP
    );
  };

  const onToggleExpand = (drawingId: string): void => {
    setExpandedDrawing(expandedDrawing === drawingId ? '' : drawingId);
  };

  const ref = useRef<HTMLDivElement>(null);

  return (
    <Box
      ref={ref}
      sx={{
        height: `calc(100% - ${DEFAULT_AREA_DRAWING_MANAGER_TAB_HEIGHT}px)`,
        width: '100%',
      }}
    >
      {!isLoading && drawings.length === 0 ? (
        <Typography
          sx={{ paddingLeft: '16px', paddingTop: '8px' }}
          variant="body1"
        >
          {t('area-drawing-manager-no-drawings')}
        </Typography>
      ) : (
        <List
          rowComponent={RowComponent}
          rowCount={listInclHeaders.length}
          rowHeight={(index): number => {
            return getItemSize(dialogWidth, index);
          }}
          rowProps={{
            listInclHeaders,
            headerList,
            drawings,
            onClickDrawing,
            onClickEdit,
            onClickDelete,
            onClickShare,
            onToggleExpand,
            expandedDrawing,
            summaryHeight,
            activeDrawing,
            onHoverDrawing,
            onHoverDrawingEnd,
            onHoverDrawingFeature,
            onHoverDrawingFeatureEnd,
            t,
          }}
          style={{
            width: '100%',
            height: `calc(100% - ${DEFAULT_ADD_BUTTON_HEIGHT}px)`,
            containerType: 'inline-size',
            containerName: 'drawingList',
          }}
        />
      )}
    </Box>
  );
};
